/**
 * MockFilesStore — web mirror of `Sources/Store/MockFilesStore.swift`.
 *
 * In-memory files store with a small sample directory tree so Brain ▸ Files
 * renders standalone in MOCK mode. `~` resolves to a fake home; navigating into a
 * directory looks the entry's absolute `path` up in the tree. Returns instantly
 * (small delay).
 */

import type { FileEntry, FilesListResponse } from '../models/brainFs';
import type { FilesStore } from './FilesStore';

const MOCK_LATENCY_MS = 250;
const HOME = '/Users/owner';

export class MockFilesStore implements FilesStore {
  private readonly tree: Record<string, FileEntry[]> = seed(HOME);

  private async delay(): Promise<void> {
    await new Promise((r) => setTimeout(r, MOCK_LATENCY_MS));
  }

  async list(path?: string): Promise<FilesListResponse> {
    await this.delay();
    const resolved = resolve(path);
    const entries = this.tree[resolved];
    if (!entries) {
      // Unknown dir: mimic an empty (still valid 200-shaped) listing.
      return { path: resolved, count: 0, entries: [] };
    }
    return { path: resolved, count: entries.length, entries };
  }
}

/** Expand `~`/undefined to the fake home; normalise a trailing slash. */
function resolve(path?: string): string {
  if (!path) return HOME;
  if (path === '~') return HOME;
  let p = path.startsWith('~/') ? HOME + path.slice(1) : path;
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

// MARK: - Seed tree

function seed(home: string): Record<string, FileEntry[]> {
  const dir = (name: string, parent: string, mtime: string, size = 128): FileEntry => ({
    name,
    type: 'dir',
    size,
    mtime,
    path: `${parent}/${name}`,
  });
  const file = (name: string, parent: string, size: number, mtime: string): FileEntry => ({
    name,
    type: 'file',
    size,
    mtime,
    path: `${parent}/${name}`,
  });

  const code = `${home}/Code`;
  const factory = `${code}/factory-app`;
  const docs = `${home}/Documents`;

  return {
    [home]: [
      dir('Code', home, '2026-07-19T22:00:00Z'),
      dir('Documents', home, '2026-07-18T09:30:00Z'),
      dir('Downloads', home, '2026-07-20T01:12:00Z', 640),
      file('.zshrc', home, 2048, '2026-07-10T08:00:00Z'),
      file('notes.md', home, 4210, '2026-07-19T18:44:00Z'),
    ],
    [code]: [
      dir('factory-app', code, '2026-07-20T02:05:00Z', 512),
      dir('claude-brain-mcp', code, '2026-07-19T23:10:00Z', 384),
      dir('trendy-stream', code, '2026-07-18T14:00:00Z', 1024),
      file('README.md', code, 890, '2026-06-30T12:00:00Z'),
    ],
    [factory]: [
      dir('Sources', factory, '2026-07-20T02:05:00Z', 256),
      dir('web', factory, '2026-07-20T02:05:00Z', 256),
      dir('FactoryApp.xcodeproj', factory, '2026-07-20T02:05:00Z'),
      file('README.md', factory, 1520, '2026-07-19T22:00:00Z'),
      file('.gitignore', factory, 210, '2026-07-01T10:00:00Z'),
    ],
    [`${factory}/Sources`]: [
      dir('App', `${factory}/Sources`, '2026-07-20T02:05:00Z'),
      dir('Models', `${factory}/Sources`, '2026-07-20T02:05:00Z'),
      dir('Store', `${factory}/Sources`, '2026-07-20T02:05:00Z'),
      dir('Views', `${factory}/Sources`, '2026-07-20T02:05:00Z'),
    ],
    [docs]: [
      file('resume.pdf', docs, 84210, '2026-05-12T09:00:00Z'),
      file('home-shortlist.md', docs, 3120, '2026-07-15T20:00:00Z'),
      dir('receipts', docs, '2026-07-17T11:00:00Z', 96),
    ],
  };
}
